import { useState } from 'react';
import {
  Button,
  Chip,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import { useNavigate } from '@tanstack/react-router';
import {
  ENTITY_TYPES,
  useEntities,
  type EntityTypeSlug,
} from '../../api/queries/entities';
import { EntityCreateDialog } from './EntityCreateDialog';

interface Props {
  bookId: string;
}

/**
 * Compact entity library for the Book Editor right-panel. Filter + search + quick-add,
 * click to navigate to the full entity detail page. This is the sidebar variant called
 * out in Phase 3 W17; the full-page view remains at /entities/$entityId.
 */
export function EntityLibrarySidebar({ bookId }: Props) {
  const [type, setType] = useState<EntityTypeSlug | ''>('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  const query = useEntities({
    scope: 'private',
    projectId: bookId,
    type: type || undefined,
    search: search.trim() || undefined,
    pageSize: 200,
  });

  return (
    <Paper
      sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Entity library
        </Typography>
        <IconButton
          size="small"
          aria-label="new entity"
          onClick={() => setCreateOpen(true)}
        >
          +
        </IconButton>
      </Stack>

      <Stack spacing={1} sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearch('')}
                  aria-label="clear"
                >
                  ✕
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <FilterableSelect
          options={ENTITY_TYPES.map((t) => ({
            value: t.value,
            label: t.label,
          }))}
          value={type}
          onChange={(newValue) =>
            setType((newValue || '') as EntityTypeSlug | '')
          }
          allowEmpty={true}
          emptyLabel="All types"
          size="small"
        />
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {query.data?.items.length ?? 0} result
          {query.data?.items.length === 1 ? '' : 's'}
        </Typography>
        <Button size="small" onClick={() => navigate({ to: '/entities' })}>
          Open full library
        </Button>
      </Stack>

      <List dense disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
        {(query.data?.items ?? []).map((e) => (
          <ListItemButton
            key={e.id}
            onClick={() =>
              navigate({
                to: '/entities/$entityId',
                params: { entityId: e.id },
              })
            }
          >
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                    {e.name}
                  </Typography>
                  <Chip size="small" label={e.entityType} />
                  {e.hasApprovedLora && (
                    <Chip size="small" color="success" label="LoRA" />
                  )}
                </Stack>
              }
              secondary={
                e.description ? (
                  <Typography variant="caption" noWrap>
                    {e.description}
                  </Typography>
                ) : undefined
              }
            />
          </ListItemButton>
        ))}
        {query.data && query.data.items.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, py: 1, display: 'block' }}
          >
            No matching entities yet.
          </Typography>
        )}
      </List>

      <EntityCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        bookId={bookId}
        onCreated={(entity) =>
          navigate({
            to: '/entities/$entityId',
            params: { entityId: entity.id },
          })
        }
      />
    </Paper>
  );
}
